import axios from 'axios';
import { OpenAPIV3 } from 'openapi-types';

interface FilteredOpenAPIOptions {
    specUrl: string;
    includePaths: string[]; // Array of path patterns to include
}

async function fetchAndFilterOpenAPISpec({
    specUrl,
    includePaths
}: FilteredOpenAPIOptions): Promise<OpenAPIV3.Document> {
    try {
        // Fetch the OpenAPI spec
        const response = await axios.get(specUrl);
        const openApiSpec = response.data as OpenAPIV3.Document;

        // Create a filtered copy of the spec
        const filteredSpec: OpenAPIV3.Document = {
            openapi: openApiSpec.openapi,
            info: openApiSpec.info,
            servers: openApiSpec.servers,
            paths: {},
            components: openApiSpec.components, // Keep components as they might be referenced
            security: openApiSpec.security,
            tags: openApiSpec.tags,
            externalDocs: openApiSpec.externalDocs
        };

        // Filter paths based on includePaths patterns.
        // Iterate patterns in order so matching paths are added in the order
        // of their first matching pattern — this drives the display order in
        // rapi-doc both for tag ordering and for path ordering within a tag.
        if (openApiSpec.paths) {
            const sourcePaths = Object.keys(openApiSpec.paths);
            const added = new Set<string>();

            includePaths.forEach(pattern => {
                const regexPattern = pattern.replace(/\*/g, '.*');
                const regex = new RegExp(`^${regexPattern}$`);

                sourcePaths.forEach(path => {
                    if (!added.has(path) && regex.test(path) && openApiSpec.paths) {
                        filteredSpec.paths[path] = openApiSpec.paths[path];
                        added.add(path);
                    }
                });
            });
        }

        // Rebuild tags in the order they are first encountered in the filtered
        // paths so the tag groups render top-to-bottom in the desired order.
        if (filteredSpec.tags) {
            const orderedTagNames: string[] = [];
            const seenTags = new Set<string>();
            const httpMethods = ['get', 'put', 'post', 'delete', 'options', 'head', 'patch', 'trace'] as const;

            Object.entries(filteredSpec.paths).forEach(([_, pathItem]) => {
                if (!pathItem) return;

                httpMethods.forEach(method => {
                    const operation = pathItem[method];
                    if (operation && typeof operation === 'object' && 'tags' in operation && operation.tags) {
                        operation.tags.forEach(tag => {
                            if (!seenTags.has(tag)) {
                                seenTags.add(tag);
                                orderedTagNames.push(tag);
                            }
                        });
                    }
                });
            });

            const tagByName = new Map(filteredSpec.tags.map(tag => [tag.name, tag]));
            filteredSpec.tags = orderedTagNames
                .map(name => tagByName.get(name))
                .filter((tag): tag is NonNullable<typeof tag> => tag !== undefined);
        }

        return filteredSpec;
    } catch (error) {
        console.error('Error fetching or filtering OpenAPI spec:', error);
        throw error;
    }
}

export { fetchAndFilterOpenAPISpec };